import { LogoutButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { useCurrentUser } from '@/hooks';
import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav';
import { Flex } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
	UserAlertDay,
	UserAlertFrequency,
} from '../../../../backend/src/types.generated';
import { BinReminderForm, BinReminderFormValues } from './form/component';
import { NotificationsManager } from './notifications-manager';

export const AlertsPage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const [loading, setLoading] = useState(false);
	const [initialValues, setInitialValues] = useState<BinReminderFormValues | undefined>(undefined);
	const formRef = useRef<FormikProps<BinReminderFormValues>>(null);
	const { isDesktop } = useLayoutContext();

	useEffect(() => {
		const deriveInitialValues = async () => {
			if (!currentUser?.id) return;

			const data = await NotificationsManager.notificationSettings({
				id: currentUser.id,
			});

			const fullTime = data?.time;
			const splitTime = fullTime ? fullTime.split(':') : undefined;
			const formattedTime = splitTime ? `${splitTime[0]}:${splitTime[1]}` : '';
			const incomingFrom = data?.from;
			const formattedFrom = incomingFrom ? new Date(incomingFrom) : null;

			setInitialValues({
				binReminderEnabled: data?.enabled ?? false,
				frequency: data?.frequency ?? UserAlertFrequency.Weekly,
				day: data?.day ?? UserAlertDay.Monday,
				time: formattedTime,
				from: formattedFrom,
				id: data?.id,
			});
		};

		deriveInitialValues();
	}, [currentUser?.id]);

	const handleSubmit = useCallback(
		async (values: BinReminderFormValues) => {
			setLoading(true);
			if (!currentUser?.id) throw new Error('Current user not found');

			try {
				const [hours, minutes] = values.time.split(':');
				const time = `${hours}:${minutes}:00`;

				await NotificationsManager.notificationSettingsValuesChanged(currentUser, {
					day: values.day,
					time,
					frequency: values.frequency,
					enabled: values.binReminderEnabled,
					from: new Date().toISOString().split('T')[0],
					id: values.id,
				});

				navigate({ to: '/home' });
			} finally {
				setLoading(false);
			}
		},
		[currentUser, navigate]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Account', backTo: '/home', children: <LogoutButton /> }}
			topNav={TopNavFor.ACCOUNT}
			footer={{
				children: (
					<StandardButton loading={loading} onClick={() => formRef.current?.submitForm()}>
						Save Alert
					</StandardButton>
				),
			}}
		>
			<ContentPanel hasTopNav width="md" gap="md">
				<CustomContentSection
					location={ContentSectionLocation.Alerts}
					placement={ContentSectionPlacement.Top}
				/>
				{initialValues && (
					<BinReminderForm
						formRef={formRef}
						onSubmit={handleSubmit}
						initialValues={initialValues}
					/>
				)}
				<CustomContentSection
					location={ContentSectionLocation.Alerts}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.Alerts}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
			{isDesktop && (
				<Flex
					direction="row"
					align="center"
					justify="center"
					pos="fixed"
					w="100%"
					bg="white"
					bottom={0}
					py="sm"
					bd="1px solid #e0e0e0"
				>
					<Flex w={200}>
						{
							<StandardButton loading={loading} onClick={() => formRef.current?.submitForm()}>
								Save Alert
							</StandardButton>
						}
					</Flex>
				</Flex>
			)}
		</PrimaryLayout>
	);
};
